/**
 * Shared API and date formatting helpers (no template-specific logic).
 */

export const formatDateForAPI = (date: string | Date | null | undefined): string | null => {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};

export const formatDateForChartAPI = (date: string | Date | null | undefined): string | undefined => {
  if (!date) return undefined;
  let d: Date;
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
      const parts = date.split('-');
      d = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  if (isNaN(d.getTime())) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDecimal = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
};

export const formatMonth = (monthStr: string): string => {
  if (!monthStr) return '';
  const parts = monthStr.split('-');
  if (parts.length === 2) {
    const month = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const year = parts[1];
    return `${month} ${year}`;
  }
  return monthStr;
};

export const parseDateString = (dateStr: string | Date | null | undefined): Date | null => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr !== 'string') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const day = parseInt(parts[2]);
    return new Date(year, month, day);
  }
  const parts = dateStr.split(/[-/]/);
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      return new Date(year, month, day);
    }
    const month = parseInt(parts[0]) - 1;
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    return new Date(year, month, day);
  }
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const getDateString = (date: any): string | null => {
  if (!date) return null;
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    return dateObj.toISOString();
  } catch {
    return null;
  }
};

export const validateTargetLaunchDate = (targetLaunchDate: any, analysisDate: any): string | null => {
  if (!targetLaunchDate || !analysisDate) return null;
  let targetDate: Date;
  let analysisDateObj: Date;
  try {
    targetDate = targetLaunchDate instanceof Date
      ? new Date(targetLaunchDate.getTime())
      : new Date(targetLaunchDate);
    analysisDateObj = analysisDate instanceof Date
      ? new Date(analysisDate.getTime())
      : new Date(analysisDate);
    if (isNaN(targetDate.getTime()) || isNaN(analysisDateObj.getTime())) return null;
    const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const analysisDateOnly = new Date(analysisDateObj.getFullYear(), analysisDateObj.getMonth(), analysisDateObj.getDate());
    const targetTime = targetDateOnly.getTime();
    const analysisTime = analysisDateOnly.getTime();
    if (targetTime < analysisTime) return 'It is not possible for Target Launch Date to be before Analysis Date';
    if (targetTime === analysisTime) return 'Target Launch Date must be after Analysis Date';
    return null;
  } catch {
    return null;
  }
};
